// Starter Tape — the song model and music helpers behind the /starter-tape
// studio. Pure data + functions only (no audio, no React) so it can be unit
// tested; starterTapeAudio.js turns a song into sound with Tone.js.
//
// A song is plain JSON:
//   { bpm, bars, stepsPerBar, rootMidi, scale,
//     tracks: [{ id, instrument, muted, notes: [{ pitch, step, length }] }] }
// `pitch` is a MIDI note number, `step` is the grid column the note starts
// on and `length` is how many steps it lasts. With stepsPerBar = 8 a step is
// an eighth note.

/* ---------- Notes ---------- */

export const NOTE_LETTERS = [
  "C",
  "C♯",
  "D",
  "E♭",
  "E",
  "F",
  "F♯",
  "G",
  "A♭",
  "A",
  "B♭",
  "B",
];

// MIDI 69 = A4 = 440 Hz.
export const midiToFreq = (midi) => 440 * Math.pow(2, (midi - 69) / 12);

export const noteLetter = (midi) => NOTE_LETTERS[((midi % 12) + 12) % 12];

// Scientific pitch name, e.g. 57 → "A3", 60 → "C4".
export const noteName = (midi) =>
  `${noteLetter(midi)}${Math.floor(midi / 12) - 1}`;

/* ---------- Scales ---------- */

// `degrees` label each step of the scale by its number counted from the
// root, the way Starter Tape shows notes ("A · 1"). Minor-key numbers are
// written plain (3, 7) rather than ♭3, ♭7 — the same convention as jianpu.
export const SCALES = {
  minorPentatonic: {
    id: "minorPentatonic",
    name: "Minor pentatonic",
    intervals: [0, 3, 5, 7, 10],
    degrees: ["1", "3", "4", "5", "7"],
  },
  majorPentatonic: {
    id: "majorPentatonic",
    name: "Major pentatonic",
    intervals: [0, 2, 4, 7, 9],
    degrees: ["1", "2", "3", "5", "6"],
  },
  // The full seven-note minor scale; chords need the notes pentatonic skips.
  naturalMinor: {
    id: "naturalMinor",
    name: "Natural minor",
    intervals: [0, 2, 3, 5, 7, 8, 10],
    degrees: ["1", "2", "3", "4", "5", "6", "7"],
  },
};

const pc = (n) => ((n % 12) + 12) % 12;

// The scale number of `midi` in the key, or null when it's outside the scale.
export const scaleDegree = (midi, rootMidi, scaleId) => {
  const scale = SCALES[scaleId];
  if (!scale) return null;
  const i = scale.intervals.indexOf(pc(midi - rootMidi));
  return i === -1 ? null : scale.degrees[i];
};

export const inScale = (midi, rootMidi, scaleId) =>
  scaleDegree(midi, rootMidi, scaleId) !== null;

// Piano-roll rows: every scale note between low and high (inclusive),
// highest first, the way a piano roll reads top to bottom.
export const scaleRows = (rootMidi, scaleId, low, high) => {
  const rows = [];
  for (let midi = high; midi >= low; midi -= 1) {
    const degree = scaleDegree(midi, rootMidi, scaleId);
    if (degree === null) continue;
    rows.push({
      midi,
      letter: noteLetter(midi),
      name: noteName(midi),
      degree,
      isRoot: degree === "1",
    });
  }
  return rows;
};

/* ---------- Instruments ---------- */

// The range each instrument's piano roll shows, and the note length a click
// places by default. Pads sit low and hold; bells sit an octave up and ring.
export const INSTRUMENTS = {
  pad: {
    id: "pad",
    name: "Pad",
    blurb: "A soft, slow sound that holds notes and fills the room.",
    low: 57, // A3
    high: 69, // A4
    defaultLength: 8,
  },
  bell: {
    id: "bell",
    name: "Bell",
    blurb: "A bright, glassy sound that rings and fades on its own.",
    low: 69, // A4
    high: 81, // A5
    defaultLength: 2,
  },
  bass: {
    id: "bass",
    name: "Bass",
    blurb: "A round, low sound that holds everything else up.",
    low: 33, // A1
    high: 45, // A2
    defaultLength: 4,
  },
  // Drums aren't pitched: each "pitch" is a drum voice (General MIDI
  // numbers), and the piano roll becomes a step grid of voices.
  drums: {
    id: "drums",
    name: "Drums",
    blurb: "Soft kick, snare and hat. Each square is one hit.",
    low: 36,
    high: 42,
    defaultLength: 1,
    unpitched: true,
  },
};

// Drum voices, top row first (as the grid shows them).
export const DRUM_VOICES = [
  { midi: 42, name: "Hat" },
  { midi: 38, name: "Snare" },
  { midi: 36, name: "Kick" },
];
export const KICK = 36;
export const SNARE = 38;
export const HAT = 42;

/* ---------- Song ---------- */

export const totalSteps = (song) => song.bars * song.stepsPerBar;

// Seconds per step at the song's tempo (a beat is a quarter note).
export const stepSeconds = (song) => 60 / song.bpm / (song.stepsPerBar / 4);

export const loopSeconds = (song) => totalSteps(song) * stepSeconds(song);

export const createSong = ({
  bpm = 72,
  bars = 4,
  stepsPerBar = 8,
  rootMidi = 57,
  scale = "minorPentatonic",
  tracks = [
    { id: "pad", instrument: "pad" },
    { id: "bell", instrument: "bell" },
  ],
} = {}) => ({
  bpm,
  bars,
  stepsPerBar,
  rootMidi,
  scale,
  tracks: tracks.map((t) => ({ muted: false, notes: [], ...t })),
});

// A gentle loop to start the sound check from: an A-minor pad held under a
// short bell phrase that ends on A, the home note.
export const demoSong = () => {
  const song = createSong();
  const pad = [
    { pitch: 57, step: 0, length: 16 }, // A3
    { pitch: 60, step: 0, length: 16 }, // C4
    { pitch: 64, step: 0, length: 16 }, // E4
    { pitch: 67, step: 16, length: 16 }, // G4
    { pitch: 60, step: 16, length: 16 }, // C4
    { pitch: 64, step: 16, length: 16 }, // E4
  ];
  const bell = [
    { pitch: 76, step: 0, length: 2 }, // E5
    { pitch: 74, step: 2, length: 2 }, // D5
    { pitch: 72, step: 4, length: 4 }, // C5
    { pitch: 76, step: 10, length: 2 }, // E5
    { pitch: 79, step: 16, length: 2 }, // G5
    { pitch: 76, step: 18, length: 2 }, // E5
    { pitch: 74, step: 20, length: 4 }, // D5
    { pitch: 69, step: 26, length: 6 }, // A4, home
  ];
  return {
    ...song,
    tracks: song.tracks.map((t) => ({
      ...t,
      notes: t.id === "pad" ? pad : bell,
    })),
  };
};

const updateTrack = (song, trackId, fn) => ({
  ...song,
  tracks: song.tracks.map((t) => (t.id === trackId ? fn(t) : t)),
});

const covers = (note, step) =>
  step >= note.step && step < note.step + note.length;

// The note on this track sounding at (pitch, step), if any.
export const noteAt = (track, pitch, step) =>
  track.notes.find((n) => n.pitch === pitch && covers(n, step)) || null;

// Add a note, clipped to the end of the loop. Notes on the same pitch that
// it would overlap are shortened or removed so a row never double-sounds.
export const addNote = (song, trackId, { pitch, step, length }) => {
  const end = totalSteps(song);
  if (step < 0 || step >= end || length < 1) return song;
  const clipped = Math.min(length, end - step);
  return updateTrack(song, trackId, (t) => {
    const kept = [];
    t.notes.forEach((n) => {
      if (n.pitch !== pitch) {
        kept.push(n);
        return;
      }
      const nEnd = n.step + n.length;
      if (nEnd <= step || n.step >= step + clipped) {
        kept.push(n); // no overlap
      } else if (n.step < step) {
        kept.push({ ...n, length: step - n.step }); // trim the earlier note
      }
      // notes starting inside the new one are dropped
    });
    kept.push({ pitch, step, length: clipped });
    kept.sort((a, b) => a.step - b.step || b.pitch - a.pitch);
    return { ...t, notes: kept };
  });
};

export const removeNote = (song, trackId, pitch, step) =>
  updateTrack(song, trackId, (t) => ({
    ...t,
    notes: t.notes.filter((n) => !(n.pitch === pitch && covers(n, step))),
  }));

// A click on the piano roll: remove the note under the cursor, or place a
// new one of `length` steps starting there.
export const toggleNote = (song, trackId, pitch, step, length) => {
  const track = song.tracks.find((t) => t.id === trackId);
  if (!track) return song;
  return noteAt(track, pitch, step)
    ? removeNote(song, trackId, pitch, step)
    : addNote(song, trackId, { pitch, step, length });
};

// Change the length of the note starting at (pitch, step), e.g. when its
// edge is dragged. It can't be shorter than one step, run past the end of
// the loop, or run into the next note on the same pitch.
export const setNoteLength = (song, trackId, pitch, step, length) =>
  updateTrack(song, trackId, (t) => {
    const note = t.notes.find((n) => n.pitch === pitch && n.step === step);
    if (!note) return t;
    const nextStart = t.notes
      .filter((n) => n.pitch === pitch && n.step > step)
      .reduce((min, n) => Math.min(min, n.step), totalSteps(song));
    const clamped = Math.max(1, Math.min(Math.round(length), nextStart - step));
    if (clamped === note.length) return t;
    return {
      ...t,
      notes: t.notes.map((n) => (n === note ? { ...n, length: clamped } : n)),
    };
  });

export const clearTrack = (song, trackId) =>
  updateTrack(song, trackId, (t) => ({ ...t, notes: [] }));

export const setMuted = (song, trackId, muted) =>
  updateTrack(song, trackId, (t) => ({ ...t, muted }));

// The pad's swell (envelope attack) in seconds: slow ≈ 1.2, fast ≈ 0.02.
export const setAttack = (song, trackId, attack) =>
  updateTrack(song, trackId, (t) => ({ ...t, attack }));

// The piano-roll rows for a track: the scale notes in its instrument's
// range, narrowed to `track.pitches` when a tape limits which notes exist.
export const trackRows = (song, track) => {
  const inst = INSTRUMENTS[track.instrument];
  const rows = inst.unpitched
    ? DRUM_VOICES.map((v) => ({
        midi: v.midi,
        letter: v.name,
        name: v.name,
        degree: "",
        isRoot: false,
      }))
    : scaleRows(song.rootMidi, song.scale, inst.low, inst.high);
  return Array.isArray(track.pitches)
    ? rows.filter((r) => track.pitches.includes(r.midi))
    : rows;
};

// Add any of `defs` ({ id, instrument }) the song doesn't have yet, as
// empty tracks — e.g. when the sandbox gains a lane after an unlock.
export const ensureTracks = (song, defs) => {
  const missing = defs.filter((d) => !song.tracks.some((t) => t.id === d.id));
  if (missing.length === 0) return song;
  return {
    ...song,
    tracks: [
      ...song.tracks,
      ...missing.map((d) => ({ muted: false, notes: [], ...d })),
    ],
  };
};

// Switch the song's scale. Pitched notes that don't belong to the new scale
// are dropped so nothing plays that the piano roll can't show.
export const setScale = (song, scaleId) => {
  if (!SCALES[scaleId]) return song;
  return {
    ...song,
    scale: scaleId,
    tracks: song.tracks.map((t) =>
      INSTRUMENTS[t.instrument] && INSTRUMENTS[t.instrument].unpitched
        ? t
        : {
            ...t,
            notes: t.notes.filter((n) =>
              inScale(n.pitch, song.rootMidi, scaleId)
            ),
          }
    ),
  };
};

// Change how many bars the loop has. Growing adds empty bars; shrinking
// drops notes that start past the new end and trims ones that ring past it.
// Sections, if any, are rebuilt evenly over the new length with the same
// names (their on/off choices reset).
export const setBars = (song, bars) => {
  const n = Math.max(1, Math.min(32, Math.round(bars)));
  if (n === song.bars) return song;
  const end = n * song.stepsPerBar;
  const resized = {
    ...song,
    bars: n,
    tracks: song.tracks.map((t) => ({
      ...t,
      notes: t.notes
        .filter((note) => note.step < end)
        .map((note) =>
          note.step + note.length > end
            ? { ...note, length: end - note.step }
            : note
        ),
    })),
  };
  if (!Array.isArray(song.sections) || song.sections.length === 0) {
    return resized;
  }
  return makeSections(
    resized,
    song.sections.map((s) => s.name).slice(0, n)
  );
};

export const setBpm = (song, bpm) => ({
  ...song,
  bpm: Math.min(140, Math.max(40, Math.round(bpm))),
});

// Everything the audio engine needs to schedule one pass of the loop, in
// steps (the engine converts steps to transport time). Muted tracks and
// notes outside the loop are left out.
/* ---------- Sections ---------- */

// A song can be split into named sections (intro, build, ...). Each section
// lists the tracks switched off while it plays, which is how the arrangement
// thins out and fills in over time. Songs without sections play every track
// everywhere.

// Split the song's bars evenly into sections named `names` (the last one
// takes any leftover bars), with every track on.
export const makeSections = (song, names) => {
  const each = Math.max(1, Math.floor(song.bars / names.length));
  return {
    ...song,
    sections: names.map((name, i) => ({
      id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      name,
      startBar: i * each,
      bars: i === names.length - 1 ? song.bars - i * each : each,
      off: [],
    })),
  };
};

export const sectionAtStep = (song, step) => {
  if (!Array.isArray(song.sections)) return null;
  const bar = Math.floor(step / song.stepsPerBar);
  return (
    song.sections.find((s) => bar >= s.startBar && bar < s.startBar + s.bars) ||
    null
  );
};

// Whether a track is switched on in the section playing at `step`.
export const trackOnAt = (song, trackId, step) => {
  const section = sectionAtStep(song, step);
  return !section || !section.off.includes(trackId);
};

export const setSectionTrack = (song, sectionId, trackId, on) => ({
  ...song,
  sections: (song.sections || []).map((s) =>
    s.id !== sectionId
      ? s
      : {
          ...s,
          off: on
            ? s.off.filter((id) => id !== trackId)
            : [...s.off.filter((id) => id !== trackId), trackId],
        }
  ),
});

// Everything the audio engine needs to schedule one pass of the loop, in
// steps (the engine converts steps to transport time). Muted tracks, notes
// outside the loop and notes starting in a section that switches their
// track off are left out; a note that rings into such a section stops at
// its edge.
export const playbackEvents = (song) => {
  const end = totalSteps(song);
  const events = [];
  song.tracks.forEach((t) => {
    if (t.muted) return;
    t.notes.forEach((n) => {
      if (n.step < 0 || n.step >= end) return;
      if (!trackOnAt(song, t.id, n.step)) return;
      let length = Math.min(n.length, end - n.step);
      for (let s = n.step + 1; s < n.step + length; s += 1) {
        if (!trackOnAt(song, t.id, s)) {
          length = s - n.step;
          break;
        }
      }
      events.push({
        trackId: t.id,
        instrument: t.instrument,
        pitch: n.pitch,
        step: n.step,
        length,
      });
    });
  });
  return events.sort((a, b) => a.step - b.step || a.pitch - b.pitch);
};

/* ---------- Sound ---------- */

// Per-track sound settings the later tapes unlock: how much of the track
// goes to the shared reverb and echo (0–1), how bright its filter is (0–1),
// its envelope (seconds) and whether the filter sweeps open across the
// loop. Tracks store only what was changed, under `sound`.
export const SOUND_DEFAULTS = {
  pad: {
    reverb: 0.4,
    delay: 0,
    brightness: 0.45,
    attack: 1.2,
    release: 3,
    sweep: false,
  },
  bell: {
    reverb: 0.4,
    delay: 0.25,
    brightness: 0.7,
    attack: 0.005,
    release: 1.6,
    sweep: false,
  },
  bass: {
    reverb: 0.05,
    delay: 0,
    brightness: 0.4,
    attack: 0.02,
    release: 0.6,
    sweep: false,
  },
  drums: {
    reverb: 0.1,
    delay: 0,
    brightness: 0.5,
    attack: 0.001,
    release: 0.1,
    sweep: false,
  },
};

const SOUND_LIMITS = {
  reverb: [0, 1],
  delay: [0, 1],
  brightness: [0, 1],
  attack: [0.005, 4],
  release: [0.1, 8],
};

// A track's full sound: defaults, then tape 1's `attack`, then `sound`.
export const soundOf = (track) => {
  const base = { ...(SOUND_DEFAULTS[track.instrument] || SOUND_DEFAULTS.pad) };
  if (Number.isFinite(track.attack)) base.attack = track.attack;
  return { ...base, ...(track.sound || {}) };
};

export const setSound = (song, trackId, patch) =>
  updateTrack(song, trackId, (t) => {
    const next = { ...(t.sound || {}) };
    Object.entries(patch).forEach(([key, value]) => {
      if (key === "sweep") {
        next.sweep = !!value;
      } else if (SOUND_LIMITS[key] && Number.isFinite(value)) {
        const [lo, hi] = SOUND_LIMITS[key];
        next[key] = Math.min(hi, Math.max(lo, value));
      }
    });
    return { ...t, sound: next };
  });

// Filter cutoff for a brightness of 0–1: 200 Hz (dark) to 6 kHz (bright),
// on a curve that feels even to the ear.
export const brightnessHz = (brightness) =>
  200 * Math.pow(30, Math.min(1, Math.max(0, brightness)));

/* ---------- Chords ---------- */

// Roman numerals for the chords built on each step of a minor scale:
// lower case = minor, upper case = major, ° = diminished.
const MINOR_ROMANS = ["i", "ii°", "III", "iv", "v", "VI", "VII"];

export const CHORD_VARIANTS = [null, "sus2", "sus4"];

// The triad built on scale step `index` (0 = the root) by stacking every
// other scale note. Only defined for seven-note scales. A `variant` swaps
// the third for the scale's 2nd ("sus2") or 4th ("sus4") above the root;
// that chord only exists when the swapped-in note is a whole step (sus2) or
// a perfect fourth (sus4) above the root. Pitches are voiced as low as
// possible at or above `low`, so neighbouring chords share notes and move
// smoothly (A–C–E to A–C–F moves one note).
export const diatonicChord = (
  song,
  index,
  low = INSTRUMENTS.pad.low,
  variant = null
) => {
  const scale = SCALES[song.scale];
  if (!scale || scale.intervals.length !== 7) return null;
  if (index < 0 || index > 6) return null;
  const at = (i) => scale.intervals[i % 7];
  const middleStep = { sus2: 1, sus4: 3 }[variant] || 2;
  const pcs = [at(index), at(index + middleStep), at(index + 4)].map((iv) =>
    pc(song.rootMidi + iv)
  );
  const middle = pc(pcs[1] - pcs[0]);
  const fifth = pc(pcs[2] - pcs[0]);
  if (variant === "sus2" && middle !== 2) return null;
  if (variant === "sus4" && (middle !== 5 || fifth !== 7)) return null;
  if (variant && variant !== "sus2" && variant !== "sus4") return null;
  const quality = variant
    ? "sus"
    : middle === 4
      ? "major"
      : fifth === 6
        ? "diminished"
        : "minor";
  const suffix = variant || { major: "", minor: "m", diminished: "°" }[quality];
  const lowPc = pc(low);
  const pitches = pcs.map((p) => low + pc(p - lowPc)).sort((a, b) => a - b);
  return {
    index,
    variant,
    roman: `${MINOR_ROMANS[index]}${variant || ""}`,
    name: `${noteLetter(pcs[0])}${suffix}`,
    quality,
    rootPc: pcs[0],
    pcs,
    pitches,
  };
};

// Which diatonic chord (plain or sus) a track is sounding at the start of
// `bar`, matched by exact pitch classes. Some sus chords share their notes
// (Gsus4 and Csus2 are both G, C, D), so notes placed by `setBarChord`
// carry a `chord` tag saying which one was picked, and the tag wins.
export const chordInBar = (song, trackId, bar) => {
  const track = song.tracks.find((t) => t.id === trackId);
  if (!track) return null;
  const step = bar * song.stepsPerBar;
  const notes = track.notes.filter(
    (n) => step >= n.step && step < n.step + n.length
  );
  const sounding = [...new Set(notes.map((n) => pc(n.pitch)))].sort(
    (a, b) => a - b
  );
  if (sounding.length !== 3) return null;
  const matches = (chord) => {
    const want = [...chord.pcs].sort((a, b) => a - b);
    return want.every((p, j) => p === sounding[j]);
  };
  const tag = notes[0].chord;
  if (
    tag &&
    notes.every(
      (n) =>
        n.chord &&
        n.chord.index === tag.index &&
        n.chord.variant === tag.variant
    )
  ) {
    const tagged = diatonicChord(
      song,
      tag.index,
      INSTRUMENTS.pad.low,
      tag.variant
    );
    if (tagged && matches(tagged)) return tagged;
  }
  for (const variant of CHORD_VARIANTS) {
    for (let i = 0; i < 7; i += 1) {
      const chord = diatonicChord(song, i, INSTRUMENTS.pad.low, variant);
      if (chord && matches(chord)) return chord;
    }
  }
  return null;
};

// Clear the notes starting inside `bar` of a track; notes from earlier bars
// that ring into it stop where it begins.
const clearBar = (song, trackId, bar) => {
  const start = bar * song.stepsPerBar;
  const end = start + song.stepsPerBar;
  return updateTrack(song, trackId, (t) => ({
    ...t,
    notes: t.notes
      .filter((n) => n.step < start || n.step >= end)
      .map((n) =>
        n.step < start && n.step + n.length > start
          ? { ...n, length: start - n.step }
          : n
      ),
  }));
};

// Put chord `index` (optionally a sus `variant`), or nothing for null, on
// `bar` of a track: the bar's notes are replaced by the chord held for the
// full bar.
export const setBarChord = (song, trackId, bar, index, variant = null) => {
  const start = bar * song.stepsPerBar;
  if (bar < 0 || start >= totalSteps(song)) return song;
  const cleared = clearBar(song, trackId, bar);
  if (index === null || index === undefined) return cleared;
  const chord = diatonicChord(song, index, INSTRUMENTS.pad.low, variant);
  if (!chord) return cleared;
  const placed = chord.pitches.reduce(
    (s, pitch) =>
      addNote(s, trackId, { pitch, step: start, length: song.stepsPerBar }),
    cleared
  );
  // Tag the new notes with the chord that was picked (see chordInBar).
  const tag = { index, variant: variant || null };
  return updateTrack(placed, trackId, (t) => ({
    ...t,
    notes: t.notes.map((n) =>
      n.step === start && chord.pitches.includes(n.pitch)
        ? { ...n, chord: tag }
        : n
    ),
  }));
};

/* ---------- Arpeggios ---------- */

// Orders to walk a chord's four tones (three chord notes + the lowest one
// an octave up, or the middle one again when that's out of range) in.
export const ARP_PATTERNS = {
  up: { name: "Up", order: [0, 1, 2, 3] },
  down: { name: "Down", order: [3, 2, 1, 0] },
  upDown: { name: "Up and down", order: [0, 1, 2, 3, 2, 1] },
};

// Fill `toTrackId` with an arpeggio of whatever chord `fromTrackId` holds
// in each bar: the chord's notes one at a time, `rate` steps apart, in the
// target instrument's range. Bars with no chord are left alone; bars with a
// chord have their target notes replaced.
export const arpeggiate = (
  song,
  fromTrackId,
  toTrackId,
  pattern = "up",
  rate = 1
) => {
  const to = song.tracks.find((t) => t.id === toTrackId);
  const order = (ARP_PATTERNS[pattern] || ARP_PATTERNS.up).order;
  if (!to || rate < 1) return song;
  const low = INSTRUMENTS[to.instrument].low;
  let next = song;
  for (let bar = 0; bar < song.bars; bar += 1) {
    const chord = chordInBar(song, fromTrackId, bar);
    if (!chord) continue;
    const voiced = diatonicChord(song, chord.index, low, chord.variant);
    // The fourth tone is the lowest note an octave up, unless that leaves
    // the instrument's range; then the middle note repeats instead.
    const top = voiced.pitches[0] + 12;
    const tones = [
      ...voiced.pitches,
      top <= INSTRUMENTS[to.instrument].high ? top : voiced.pitches[1],
    ];
    next = clearBar(next, toTrackId, bar);
    const start = bar * song.stepsPerBar;
    for (let i = 0; i * rate < song.stepsPerBar; i += 1) {
      next = addNote(next, toTrackId, {
        pitch: tones[order[i % order.length]],
        step: start + i * rate,
        length: rate,
      });
    }
  }
  return next;
};

/* ---------- Saving ---------- */

// Loose shape check for a song read back from storage; anything that fails
// it is ignored and the studio falls back to the demo loop.
export const isValidSong = (song) =>
  !!song &&
  typeof song === "object" &&
  Number.isFinite(song.bpm) &&
  Number.isInteger(song.bars) &&
  Number.isInteger(song.stepsPerBar) &&
  Number.isFinite(song.rootMidi) &&
  !!SCALES[song.scale] &&
  Array.isArray(song.tracks) &&
  song.tracks.every(
    (t) =>
      t &&
      typeof t.id === "string" &&
      !!INSTRUMENTS[t.instrument] &&
      Array.isArray(t.notes) &&
      t.notes.every(
        (n) =>
          n &&
          Number.isFinite(n.pitch) &&
          Number.isInteger(n.step) &&
          Number.isInteger(n.length)
      )
  ) &&
  (song.sections === undefined ||
    (Array.isArray(song.sections) &&
      song.sections.every(
        (sec) =>
          sec &&
          typeof sec.id === "string" &&
          typeof sec.name === "string" &&
          Number.isInteger(sec.startBar) &&
          Number.isInteger(sec.bars) &&
          Array.isArray(sec.off)
      )));
